import React, { useMemo, useState } from 'react';
import { Award, AlertTriangle, CheckCircle2, XCircle, BarChart3, ChevronDown, ChevronRight, BookOpen, LayoutList, Info, Eye, EyeOff, TrendingUp } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { useQualityWorkspaceState } from './useQualityWorkspaceState';
import { buildQualityReport, QuestionQualityScore, QualityDimension, QuestionQualityReport } from '../../../governance/questionQualityEngine';
type FlagFilter = 'all' | 'generic-feedback' | 'no-explanation' | 'weak-distractor' | 'rule-violation' | 'tier-mismatch' | 'poor' | 'excellent';
export function QuestionQualityPanel({
  report: reportProp
}: {
  report?: QuestionQualityReport;
}) {
  const {
    filteredRecords
  } = useQualityWorkspaceState();
  // NEW-BUG-2 fix: use pre-computed report from QualityWorkspace if provided,
  // otherwise fall back to computing from filteredRecords (e.g. when used standalone)
  const report = useMemo(() => {
    if (reportProp) return reportProp;
    const questions = filteredRecords.map(r => r.question);
    return buildQualityReport(questions);
  }, [reportProp, filteredRecords]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [expandedQuestionId, setExpandedQuestionId] = useState<string | null>(null);
  const [flagFilter, setFlagFilter] = useState<FlagFilter>('all');
  const [showExplanationPreview, setShowExplanationPreview] = useState(false);
  const filteredScores = useMemo(() => {
    let scores = [...report.scores];
    switch (flagFilter) {
      case 'generic-feedback':
        scores = scores.filter(s => s.flags.some(f => f.label === 'Generic feedback'));
        break;
      case 'no-explanation':
        scores = scores.filter(s => s.flags.some(f => f.label === 'No explanation'));
        break;
      case 'weak-distractor':
        scores = scores.filter(s => s.flags.some(f => f.label === 'Weak distractor' || f.label === 'Generic distractor'));
        break;
      case 'rule-violation':
        scores = scores.filter(s => s.flags.some(f => f.label === 'Rule violation'));
        break;
      case 'tier-mismatch':
        scores = scores.filter(s => s.flags.some(f => f.label === 'Tier mismatch' || f.label === 'Tier drift'));
        break;
      case 'poor':
        scores = scores.filter(s => s.grade === 'poor');
        break;
      case 'excellent':
        scores = scores.filter(s => s.grade === 'excellent');
        break;
    }
    return scores.sort((a, b) => sortOrder === 'asc' ? a.totalScore - b.totalScore : b.totalScore - a.totalScore);
  }, [report.scores, sortOrder, flagFilter]);
  // Flag counts for filter buttons
  const flagCounts = useMemo(() => ({
    genericFeedback: report.scores.filter(s => s.flags.some(f => f.label === 'Generic feedback')).length,
    noExplanation: report.scores.filter(s => s.flags.some(f => f.label === 'No explanation')).length,
    weakDistractor: report.scores.filter(s => s.flags.some(f => f.label === 'Weak distractor' || f.label === 'Generic distractor')).length,
    ruleViolation: report.scores.filter(s => s.flags.some(f => f.label === 'Rule violation')).length,
    tierMismatch: report.scores.filter(s => s.flags.some(f => f.label === 'Tier mismatch' || f.label === 'Tier drift')).length,
    poor: report.scores.filter(s => s.grade === 'poor').length,
    excellent: report.scores.filter(s => s.grade === 'excellent').length
  }), [report.scores]);
  return <div className="space-y-6" data-id="element-2344">
      {/* Summary Header */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2345">
        <Card className="p-4 flex flex-col justify-between bg-slate-50 border-slate-200" data-id="element-2346">
          <div data-id="element-2347">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-2348">
              Average Quality Score
            </p>
            <div className="mt-2 flex items-baseline gap-2" data-id="element-2349">
              <span className={`text-3xl font-black ${getScoreColor(report.avgScore)}`} data-id="element-2350">
                {report.avgScore}
              </span>
              <span className="text-sm text-slate-400" data-id="element-2351">/ 100</span>
            </div>
          </div>
          <div className="mt-4 h-2 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-2352">
            <div className={`h-full ${getScoreBgColor(report.avgScore)}`} style={{
            width: `${report.avgScore}%`
          }} data-id="element-2353" />
          </div>
        </Card>

        <Card className="p-4 col-span-1 md:col-span-3" data-id="element-2354">
          <div className="flex items-center justify-between mb-4" data-id="element-2355">
            <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-2356">
              <BarChart3 className="w-4 h-4 text-indigo-500" data-id="element-2357" />
              Quality Distribution
            </h3>
            <div className="flex gap-2 text-xs flex-wrap" data-id="element-2358">
              <Badge variant="success" size="sm" data-id="element-2359">
                {report.distribution.excellent} Excellent
              </Badge>
              <Badge variant="info" size="sm" data-id="element-2360">
                {report.distribution.good} Good
              </Badge>
              <Badge variant="warning" size="sm" data-id="element-2361">
                {report.distribution.needsWork} Needs Work
              </Badge>
              <Badge variant="danger" size="sm" data-id="element-2362">
                {report.distribution.poor} Poor
              </Badge>
            </div>
          </div>
          <div className="flex h-8 w-full rounded-lg overflow-hidden" data-id="element-2363">
            {report.totalQuestions > 0 ? <>
                <div style={{
              width: `${report.distribution.excellent / report.totalQuestions * 100}%`
            }} className="bg-emerald-500 h-full" title="Excellent" data-id="element-2364" />
                <div style={{
              width: `${report.distribution.good / report.totalQuestions * 100}%`
            }} className="bg-blue-500 h-full" title="Good" data-id="element-2365" />
                <div style={{
              width: `${report.distribution.needsWork / report.totalQuestions * 100}%`
            }} className="bg-amber-500 h-full" title="Needs Work" data-id="element-2366" />
                <div style={{
              width: `${report.distribution.poor / report.totalQuestions * 100}%`
            }} className="bg-rose-500 h-full" title="Poor" data-id="element-2367" />
              </> : <div className="w-full h-full bg-slate-100 flex items-center justify-center text-xs text-slate-400" data-id="element-2368">
                No data
              </div>}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2369">
        {/* Left Column */}
        <div className="space-y-6" data-id="element-2370">
          {/* Module Breakdown */}
          <Card className="p-0 overflow-hidden" data-id="element-2371">
            <div className="p-4 border-b border-slate-100 bg-slate-50/50" data-id="element-2372">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-2373">
                <LayoutList className="w-4 h-4 text-slate-500" data-id="element-2374" />
                Module Breakdown
              </h3>
            </div>
            <div className="divide-y divide-slate-100" data-id="element-2375">
              {report.byModule.map(mod => {
              const grade = mod.avgScore >= 80 ? 'excellent' : mod.avgScore >= 60 ? 'good' : mod.avgScore >= 40 ? 'needs-work' : 'poor';
              return <div key={mod.module} className="p-3" data-id="element-2376">
                    <div className="flex items-center justify-between mb-1.5" data-id="element-2377">
                      <div data-id="element-2378">
                        <div className="font-medium text-slate-900 text-sm" data-id="element-2379">
                          {mod.module}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-2380">
                          {mod.questionCount} questions
                        </div>
                      </div>
                      <Badge variant={getGradeVariant(grade)} data-id="element-2381">
                        Avg: {mod.avgScore}
                      </Badge>
                    </div>
                    {/* Mini score bar */}
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-2382">
                      <div className={`h-full rounded-full ${getScoreBgColor(mod.avgScore)}`} style={{
                    width: `${mod.avgScore}%`
                  }} data-id="element-2383" />
                    </div>
                    {/* Top issues for this module */}
                    {mod.topIssues.length > 0 && <div className="mt-2 space-y-0.5" data-id="element-2384">
                        {mod.topIssues.slice(0, 2).map((issue, idx) => <div key={idx} className="text-xs text-slate-500 flex items-center gap-1 truncate" data-id="element-2385">
                            <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" data-id="element-2386" />
                            <span className="truncate" data-id="element-2387">{issue.issue}</span>
                            <span className="shrink-0 text-slate-400" data-id="element-2388">
                              ×{issue.count}
                            </span>
                          </div>)}
                      </div>}
                  </div>;
            })}
            </div>
          </Card>

          {/* Top Issues */}
          <Card className="p-0 overflow-hidden" data-id="element-2389">
            <div className="p-4 border-b border-slate-100 bg-slate-50/50" data-id="element-2390">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-2391">
                <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-2392" />
                Top Quality Issues
              </h3>
            </div>
            <div className="divide-y divide-slate-100" data-id="element-2393">
              {report.topIssues.length > 0 ? report.topIssues.map((issue, idx) => <div key={idx} className="p-3" data-id="element-2394">
                    <div className="flex justify-between items-start gap-2" data-id="element-2395">
                      <span className="text-xs text-slate-700 font-medium leading-snug" data-id="element-2396">
                        {issue.issue}
                      </span>
                      <Badge variant="neutral" size="sm" className="shrink-0" data-id="element-2397">
                        {issue.count}
                      </Badge>
                    </div>
                    <div className="mt-1" data-id="element-2398">
                      <Badge variant={issue.severity === 'critical' ? 'danger' : issue.severity === 'warning' ? 'warning' : 'info'} size="sm" data-id="element-2399">
                        {issue.severity}
                      </Badge>
                    </div>
                  </div>) : <div className="p-4 text-center text-sm text-slate-500" data-id="element-2400">
                  No common issues found
                </div>}
            </div>
          </Card>
        </div>

        {/* Right Column: Question List */}
        <div className="lg:col-span-2 space-y-4" data-id="element-2401">
          <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-2402">
            <h3 className="font-bold text-slate-900 text-lg" data-id="element-2403">
              Question Analysis
            </h3>
            <div className="flex items-center gap-2 flex-wrap" data-id="element-2404">
              <button onClick={() => setShowExplanationPreview(v => !v)} className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white transition-colors" data-id="element-2405">
                {showExplanationPreview ? <EyeOff className="w-3.5 h-3.5" data-id="element-2406" /> : <Eye className="w-3.5 h-3.5" data-id="element-2407" />}
                {showExplanationPreview ? 'Hide' : 'Show'} Explanation
              </button>
              <div className="flex bg-white rounded-lg border border-slate-200 p-0.5" data-id="element-2408">
                <button onClick={() => setSortOrder('asc')} className={`px-2 py-1 text-xs font-medium rounded ${sortOrder === 'asc' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2409">
                  Lowest First
                </button>
                <button onClick={() => setSortOrder('desc')} className={`px-2 py-1 text-xs font-medium rounded ${sortOrder === 'desc' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2410">
                  Highest First
                </button>
              </div>
            </div>
          </div>

          {/* Flag Filter Pills */}
          <div className="flex flex-wrap gap-2" data-id="element-2411">
            {([{
            id: 'all',
            label: 'All',
            count: report.scores.length
          }, {
            id: 'generic-feedback',
            label: '⚠ Generic Feedback',
            count: flagCounts.genericFeedback
          }, {
            id: 'no-explanation',
            label: '✗ No Explanation',
            count: flagCounts.noExplanation
          }, {
            id: 'weak-distractor',
            label: '⚠ Weak Distractor',
            count: flagCounts.weakDistractor
          }, {
            id: 'rule-violation',
            label: '✗ Rule Violation',
            count: flagCounts.ruleViolation
          }, {
            id: 'tier-mismatch',
            label: '📊 Tier Drift',
            count: flagCounts.tierMismatch
          }, {
            id: 'poor',
            label: '🔴 Poor',
            count: flagCounts.poor
          }, {
            id: 'excellent',
            label: '🟢 Excellent',
            count: flagCounts.excellent
          }] as Array<{
            id: FlagFilter;
            label: string;
            count: number;
          }>).map(f => <button key={f.id} onClick={() => setFlagFilter(f.id)} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${flagFilter === f.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`} data-id="element-2412">
                {f.label}
                <span className={`text-[10px] font-bold px-1 py-0.5 rounded-full ${flagFilter === f.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-2413">
                  {f.count}
                </span>
              </button>)}
          </div>

          <div className="space-y-3" data-id="element-2414">
            {filteredScores.slice(0, 50).map(score => <QuestionScoreCard key={score.questionId} score={score} isExpanded={expandedQuestionId === score.questionId} showExplanationPreview={showExplanationPreview} onToggle={() => setExpandedQuestionId(expandedQuestionId === score.questionId ? null : score.questionId)} data-id="element-2415" />)}
            {filteredScores.length === 0 && <Card className="p-8 text-center" data-id="element-2416">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" data-id="element-2417" />
                <p className="font-bold text-slate-900" data-id="element-2418">
                  No questions match this filter
                </p>
                <p className="text-sm text-slate-500 mt-1" data-id="element-2419">
                  Try a different flag filter or clear the selection.
                </p>
              </Card>}
            {filteredScores.length > 50 && <div className="text-center py-4 text-sm text-slate-500" data-id="element-2420">
                Showing 50 of {filteredScores.length} questions. Use filters to
                narrow results.
              </div>}
          </div>
        </div>
      </div>
    </div>;
}
function QuestionScoreCard({
  score,
  isExpanded,
  onToggle,
  showExplanationPreview
}: {
  score: QuestionQualityScore;
  isExpanded: boolean;
  onToggle: () => void;
  showExplanationPreview: boolean;
}) {
  const hasGenericFeedback = score.flags.some(f => f.label === 'Generic feedback');
  const hasGenericDistractor = score.flags.some(f => f.label === 'Generic distractor');
  return <Card className={`transition-all duration-200 ${isExpanded ? 'ring-2 ring-indigo-500 shadow-md' : hasGenericFeedback ? 'ring-1 ring-rose-300 bg-rose-50/30' : 'hover:shadow-sm'}`} data-id="element-2421">
      {hasGenericFeedback && !isExpanded && <div className="px-4 pt-3 pb-0" data-id="element-2422">
          <div className="flex items-center gap-2 text-xs font-semibold text-rose-700 bg-rose-100 border border-rose-200 rounded-lg px-3 py-1.5" data-id="element-2423">
            <XCircle className="w-3.5 h-3.5 shrink-0" data-id="element-2424" />
            Boilerplate feedback — does not explain the concept or calculation
          </div>
        </div>}

      <div className="p-4 cursor-pointer flex items-start gap-4" onClick={onToggle} data-id="element-2425">
        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg ${getScoreBgColor(score.totalScore)} text-white`} data-id="element-2426">
          {score.totalScore}
        </div>

        <div className="flex-1 min-w-0" data-id="element-2427">
          <div className="flex items-center justify-between mb-1" data-id="element-2428">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-2429">
              <code className="text-sm font-bold text-slate-900" data-id="element-2430">
                {score.questionId}
              </code>
              <Badge variant="outline" size="sm" data-id="element-2431">
                {score.module}
              </Badge>
              <Badge variant="outline" size="sm" data-id="element-2432">
                {score.levelCode}
              </Badge>
            </div>
            {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" data-id="element-2433" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" data-id="element-2434" />}
          </div>

          <div className="flex flex-wrap gap-2 mt-2" data-id="element-2435">
            {score.flags.map((flag, idx) => <Badge key={idx} variant={flag.type === 'critical' ? 'danger' : flag.type === 'warning' ? 'warning' : 'success'} size="sm" data-id="element-2436">
                {flag.label}
              </Badge>)}
            {score.flags.length === 0 && <span className="text-xs text-slate-400 italic" data-id="element-2437">
                No major flags
              </span>}
          </div>

          {/* Explanation preview */}
          {showExplanationPreview && <div className={`mt-2 text-xs rounded-lg px-3 py-2 border ${hasGenericFeedback ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-id="element-2438">
              <span className="font-semibold" data-id="element-2439">Explanation: </span>
              {score.explanationText ? <span className={`line-clamp-2 ${hasGenericFeedback ? 'italic' : ''}`} data-id="element-2440">
                  {score.explanationText}
                </span> : <span className="italic opacity-60" data-id="element-2441">[No explanation text]</span>}
            </div>}
        </div>

        {/* Mini Dimension Bars - UPDATED for new scoring model */}
        <div className="hidden lg:flex gap-1 items-end h-10 w-36 pb-1" data-id="element-2442">
          <DimensionBar score={score.dimensions.explanationClarity} label="Clarity" data-id="element-2443" />
          <DimensionBar score={score.dimensions.didacticDepth} label="Depth" data-id="element-2444" />
          <DimensionBar score={score.dimensions.distractors} label="Dist" data-id="element-2445" />
          <DimensionBar score={score.dimensions.metadata} label="Meta" data-id="element-2446" />
          <DimensionBar score={score.dimensions.formatting} label="Fmt" data-id="element-2447" />
          <DimensionBar score={score.dimensions.tierAlignment} label="Tier" data-id="element-2448" />
        </div>
      </div>

      {isExpanded && <div className="border-t border-slate-100 p-4 bg-slate-50/50 rounded-b-xl space-y-4" data-id="element-2449">
          {hasGenericFeedback && <div className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded-xl p-4" data-id="element-2450">
              <XCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" data-id="element-2451" />
              <div data-id="element-2452">
                <p className="text-sm font-bold text-rose-800" data-id="element-2453">
                  Boilerplate Explanation Detected
                </p>
                <p className="text-xs text-rose-700 mt-1" data-id="element-2454">
                  This explanation is generic and non-didactic. It does not
                  explain the concept, show the calculation, or reference the
                  specific misconception. Per Master Rulebook rule MASTER_2_2,
                  every explanation must be educational and question-specific.
                </p>
                <p className="text-xs text-rose-600 mt-2 font-medium" data-id="element-2455">
                  Required: Show the calculation steps, name the concept, and
                  explain why the wrong answers are wrong.
                </p>
              </div>
            </div>}

          {hasGenericDistractor && <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-2456">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-2457" />
              <div data-id="element-2458">
                <p className="text-sm font-bold text-amber-800" data-id="element-2459">
                  Generic Distractor Explanations
                </p>
                <p className="text-xs text-amber-700 mt-1" data-id="element-2460">
                  One or more distractor explanations are generic. Per Master
                  Rulebook rule MASTER_2_3, each wrong option must explain the
                  specific misconception it represents.
                </p>
              </div>
            </div>}

          {/* UPDATED: New dimension breakdown (20+30+25+10+10+5=100) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2461">
            <DimensionDetail title="Explanation Clarity" dimension={score.dimensions.explanationClarity} icon={BookOpen} data-id="element-2462" />
            <DimensionDetail title="Didactic Depth" dimension={score.dimensions.didacticDepth} icon={TrendingUp} data-id="element-2463" />
            <DimensionDetail title="Distractors" dimension={score.dimensions.distractors} icon={AlertTriangle} data-id="element-2464" />
            <DimensionDetail title="Metadata" dimension={score.dimensions.metadata} icon={Info} data-id="element-2465" />
            <DimensionDetail title="Formatting" dimension={score.dimensions.formatting} icon={LayoutList} data-id="element-2466" />
            <DimensionDetail title="Tier Alignment" dimension={score.dimensions.tierAlignment} icon={BarChart3} data-id="element-2467" />
          </div>

          {/* Tier alignment detail */}
          {score.tierAlignmentResult.issues.length > 0 && <div data-id="element-2468">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-2469">
                Tier Alignment Issues (Band {score.tierAlignmentResult.band} ·{' '}
                {score.tierAlignmentResult.levelCode})
              </h4>
              <div className="space-y-2" data-id="element-2470">
                {score.tierAlignmentResult.issues.map((issue, idx) => <div key={idx} className={`flex items-start gap-2 text-sm p-2 rounded border ${issue.severity === 'critical' ? 'bg-rose-50 border-rose-100' : issue.severity === 'warning' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100'}`} data-id="element-2471">
                    <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${issue.severity === 'critical' ? 'text-rose-500' : issue.severity === 'warning' ? 'text-amber-500' : 'text-blue-400'}`} data-id="element-2472" />
                    <div data-id="element-2473">
                      <span className={issue.severity === 'critical' ? 'text-rose-700' : issue.severity === 'warning' ? 'text-amber-700' : 'text-blue-700'} data-id="element-2474">
                        {issue.message}
                      </span>
                      {issue.detail && <p className="text-xs text-slate-500 mt-0.5" data-id="element-2475">
                          {issue.detail}
                        </p>}
                      <span className="ml-2 text-xs text-slate-400 font-mono" data-id="element-2476">
                        {issue.ruleId}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>}

          {score.ruleIssues.length > 0 && <div data-id="element-2477">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-2478">
                Governance Violations
              </h4>
              <div className="space-y-2" data-id="element-2479">
                {score.ruleIssues.map((issue, idx) => <div key={idx} className="flex items-start gap-2 text-sm bg-white p-2 rounded border border-rose-100" data-id="element-2480">
                    <XCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" data-id="element-2481" />
                    <div data-id="element-2482">
                      <span className="text-rose-700" data-id="element-2483">{issue.message}</span>
                      <span className="ml-2 text-xs text-rose-400 font-mono" data-id="element-2484">
                        {issue.rule}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>}

          {score.distractorAnalysis.length > 0 && <div data-id="element-2485">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-2486">
                Distractor Analysis
              </h4>
              <div className="space-y-2" data-id="element-2487">
                {score.distractorAnalysis.map((issue, idx) => <div key={idx} className="flex items-start gap-2 text-sm bg-white p-2 rounded border border-amber-100" data-id="element-2488">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-2489" />
                    <span className="text-amber-700" data-id="element-2490">{issue.issue}</span>
                  </div>)}
              </div>
            </div>}
        </div>}
    </Card>;
}
function DimensionBar({
  score,
  label
}: {
  score: QualityDimension;
  label: string;
}) {
  const percent = score.score / score.maxScore * 100;
  const colorClass = percent >= 80 ? 'bg-emerald-400' : percent >= 50 ? 'bg-amber-400' : 'bg-rose-400';
  return <div className="flex-1 flex flex-col justify-end items-center gap-1 h-full group relative" data-id="element-2491">
      <div className={`w-full rounded-t-sm ${colorClass}`} style={{
      height: `${percent}%`
    }} data-id="element-2492" />
      <div className="absolute bottom-full mb-1 hidden group-hover:block bg-slate-800 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-10" data-id="element-2493">
        {label}: {score.score}/{score.maxScore}
      </div>
    </div>;
}
function DimensionDetail({
  title,
  dimension,
  icon: Icon
}: {
  title: string;
  dimension: QualityDimension;
  icon: any;
}) {
  const percent = Math.round(dimension.score / dimension.maxScore * 100);
  const isGood = percent >= 80;
  return <div className="bg-white p-3 rounded-lg border border-slate-200" data-id="element-2494">
      <div className="flex items-center justify-between mb-2" data-id="element-2495">
        <div className="flex items-center gap-2" data-id="element-2496">
          <Icon className="w-4 h-4 text-slate-400" data-id="element-2497" />
          <span className="font-semibold text-sm text-slate-700" data-id="element-2498">{title}</span>
        </div>
        <span className={`text-xs font-bold ${isGood ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-2499">
          {dimension.score}/{dimension.maxScore}
        </span>
      </div>
      {dimension.issues.length > 0 ? <ul className="space-y-1" data-id="element-2500">
          {dimension.issues.map((issue, idx) => <li key={idx} className="text-xs text-rose-600 flex items-start gap-1.5" data-id="element-2501">
              <span className="mt-1 w-1 h-1 rounded-full bg-rose-400 shrink-0" data-id="element-2502" />
              {issue}
            </li>)}
        </ul> : <p className="text-xs text-emerald-600 flex items-center gap-1" data-id="element-2503">
          <CheckCircle2 className="w-3 h-3" data-id="element-2504" />
          No issues found
        </p>}
    </div>;
}
function getScoreColor(score: number) {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-blue-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-rose-600';
}
function getScoreBgColor(score: number) {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-blue-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-rose-500';
}
function getGradeVariant(grade: string): 'success' | 'info' | 'warning' | 'danger' {
  switch (grade) {
    case 'excellent':
      return 'success';
    case 'good':
      return 'info';
    case 'needs-work':
      return 'warning';
    case 'poor':
      return 'danger';
    default:
      return 'info';
  }
}